import { baseApi, apiTags } from "../baseApi";
import type { Application } from "../types/api.types";
import type { LaravelQueryBuilder } from '../types/laravelRequest';

import { loadFunnelStatisticsDataset } from "./loadFunnelStatisticsDataset";
import type { FunnelStatisticsDataset } from "./types";

export type FunnelApplicationsParams = {
    start: string;
    end: string;
    selectedResourceIds: number[];
};

const FUNNEL_STATISTICS_CACHE_SEC = 3600 * 6;

export const funnelApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getFunnelStatisticsDataset: builder.query<
            FunnelStatisticsDataset,
            { withFinanceGroup: boolean; domainUrl?: string | null }
        >({
            queryFn: async ({ withFinanceGroup, domainUrl }) => {
                try {
                    const data = await loadFunnelStatisticsDataset(withFinanceGroup, domainUrl);
                    return { data };
                } catch (error: any) {
                    const err = error as { status?: number; responseData?: any; message?: string };
                    return {
                        error: {
                            status: typeof err?.status === "number" ? err.status : 500,
                            data: err?.responseData ?? err?.message ?? error,
                        },
                    };
                }
            },
            providesTags: [{ type: apiTags.Funnel, id: "STATISTICS_DATASET" }],
            keepUnusedDataFor: FUNNEL_STATISTICS_CACHE_SEC,
        }),
        getFunnelApplications: builder.query<Application[], FunnelApplicationsParams>({
            query: ({ start, end, selectedResourceIds }) => ({
                resource: "crm-application",
                action: "index",
                method: "GET",
                useAll: true,
                queryBuilder: (query: LaravelQueryBuilder) => {
                    query.with("resource").orderBy("created_at", "ASC");
                    if (start && end) {
                        query.whereDate("created_at", ">=", start);
                        query.whereDate("created_at", "<=", end);
                    }
                    if (selectedResourceIds.length > 0) {
                        query.whereIn("resources_id", selectedResourceIds);
                    }
                    return query;
                },
            }),
            providesTags: [{ type: apiTags.Funnel, id: "APPLICATIONS" }],
        }),
        getFunnelResources: builder.query<Array<{ id: number; text: string; is_active?: boolean }>, void>({
            query: () => ({
                resource: "crm-resource",
                action: "index",
                method: "GET",
                useAll: true,
            }),
            providesTags: [{ type: apiTags.Funnel, id: "RESOURCES" }],
        }),
        getActiveUserValuations: builder.query<unknown[], Record<string, unknown> | void>({
            query: (params) => ({
                resource: "active-user-valuation",
                action: "index",
                method: "GET",
                params: params ?? undefined,
            }),
            providesTags: [{ type: apiTags.Funnel, id: "ACTIVE_USER_VALUATIONS" }],
        }),
        updateClientServiceArchive: builder.mutation<
            unknown,
            Record<string, unknown>
        >({
            query: (payload) => ({
                resource: "crm-client-services",
                action: "update",
                method: "PUT",
                id: 0,
                data: payload,
            }),
            invalidatesTags: [{ type: apiTags.Funnel, id: "APPLICATIONS" }],
        }),
    }),
});

export const {
    useGetFunnelStatisticsDatasetQuery,
    useLazyGetFunnelStatisticsDatasetQuery,
    useGetFunnelApplicationsQuery,
    useGetFunnelResourcesQuery,
    useGetActiveUserValuationsQuery,
    useLazyGetActiveUserValuationsQuery,
    useUpdateClientServiceArchiveMutation,
} = funnelApi;
